import React, { useMemo, useState, useCallback } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import { FormModal } from '@ubeya/shared-web/components';
import { schedulingHourlyTimeOptions } from 'shared/atoms/scheduling';
import { AddTimeAvailabilityModalFields } from './AddTimeAvailabilityModalFields';
import { AVAILABILITY_REQUEST_TYPE } from 'scheduling/constants';

const H3BoldWhite = styled.h3`
  font-weight: 500;
  text-align: center;
  font-size: 20px;
`;

const EmployeeName = styled.h3`
  font-weight: 500;
  text-align: center;
  font-size: 20px;
  margin-top: 3px;
`;

const CurrentDate = styled.div`
  margin-top: 2px;
  font-size: 16px;
  text-align: center;
`;

export const AddTimeAvailabilityModal = ({
  t,
  employeeName,
  dateFormat,
  timeOffRequestOptions,
  mappedTimeOffRequestOptions,
  date: selectedDate,
  onClose,
  onSave,
  daysOptions
}) => {
  const [selectedDays, setSelectedDays] = useState(() =>
    daysOptions.reduce(
      (carry, item) => ({
        ...carry,
        [item.date]: item.momentDate.isSame(moment(selectedDate), 'date')
      }),
      {}
    )
  );
  const [isLoading, setIsLoading] = useState(false);

  const hourlyTimeOptions = useRecoilValue(schedulingHourlyTimeOptions);

  const initialValues = useMemo(() => {
    const startTime = moment();
    const endTime = moment();
    const status = AVAILABILITY_REQUEST_TYPE.PREFER;
    const absenceType = null;

    startTime.set({ hour: hourlyTimeOptions?.startOfDaysHours, minute: 0 });
    endTime.set({ hour: hourlyTimeOptions?.endOfDaysHours, minute: 0 });

    return { startTime: startTime.toDate(), endTime: endTime.toDate(), status, absenceType };
  }, [hourlyTimeOptions]);

  const getSelectedDates = useCallback(() => Object.keys(selectedDays).filter((key) => !!selectedDays[key]), [
    selectedDays
  ]);

  const onSaveInternal = useCallback(
    async (values) => {
      setIsLoading(true);
      await onSave?.({
        ...values,
        dates: getSelectedDates()
      });
      setIsLoading(false);
    },
    [getSelectedDates, onSave]
  );

  const timePickerOptions = useMemo(() => hourlyTimeOptions?.defaultTimePickerOptions?.map((date) => moment(date)), [
    hourlyTimeOptions?.defaultTimePickerOptions
  ]);

  return (
    <FormModal
      size="long"
      onClose={onClose}
      confirmText={t('save')}
      isPaddingContent={false}
      loadingConfirm={isLoading}
      disabledOnPristine={false}
      closeOnClickOutside
      title={
        <>
          <H3BoldWhite>{t('addTimeAvailability')}</H3BoldWhite>
          <EmployeeName>{employeeName}</EmployeeName>
          <CurrentDate>{moment(selectedDate).format(`ddd ${dateFormat}`).toUpperCase()}</CurrentDate>
        </>
      }
      onSubmit={onSaveInternal}
      initialValues={initialValues}>
      {({ valid, values }) => (
        <AddTimeAvailabilityModalFields
          t={t}
          valid={valid}
          values={values}
          selectedDays={selectedDays}
          daysOptions={daysOptions}
          timeOffRequestOptions={timeOffRequestOptions}
          mappedTimeOffRequestOptions={mappedTimeOffRequestOptions}
          setSelectedDays={setSelectedDays}
          timePickerOptions={timePickerOptions}
          getSelectedDates={getSelectedDates}
          selectedDate={selectedDate}
        />
      )}
    </FormModal>
  );
};
